import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Megaphone } from "lucide-react";
import { toast } from "sonner";

export default function RosterNoticeDialog({ teamLetters, roster }) {
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState("all");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);

  const active = roster.filter((r) => (r.status || "").toLowerCase() !== "withdrew");
  const count = audience === "all" ? active.length : active.filter((r) => r.team_letter === audience.split(":")[1]).length;

  const send = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Add a subject and a message"); return; }
    if (!window.confirm(`Send "${subject}" to ${count} student${count === 1 ? "" : "s"}${sendEmail ? " by email and in-app" : " in-app only"}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/roster/notice", { subject, message, audience, send_email: sendEmail });
      toast.success(`Notice sent · ${data.queued} email${data.queued === 1 ? "" : "s"} queued${data.in_app ? " · shown in-app" : ""}`);
      setOpen(false); setSubject(""); setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send notice");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="roster-notice-btn">
        <Megaphone className="w-4 h-4 mr-2" /> Send notice
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="roster-notice-dialog">
          <DialogHeader>
            <DialogTitle>Send a notice</DialogTitle>
            <DialogDescription>A one-off message to students — e.g. "please disregard the earlier email". Goes by email to roster addresses and as an in-app banner to anyone who has registered.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Audience</label>
              <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full border rounded-md h-9 px-2 text-sm mt-1" data-testid="roster-notice-audience">
                <option value="all">All students</option>
                {teamLetters.map((l) => <option key={l} value={`team:${l}`}>Team {l}</option>)}
              </select>
              <div className="text-[11px] text-slate-500 mt-1" data-testid="roster-notice-count">{count} student{count === 1 ? "" : "s"}</div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Subject</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Please disregard the earlier email" className="mt-1" data-testid="roster-notice-subject" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Message</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
                className="w-full border rounded-md p-2 text-sm mt-1" placeholder="Earlier today a 'We missed you' email was sent in error…" data-testid="roster-notice-message" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="roster-notice-email-toggle" />
              Also send by email
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={send} disabled={busy || !count} className="bg-[#0F1F2E] hover:bg-[#1a2f42]" data-testid="roster-notice-send">
              {busy ? "Sending…" : `Send to ${count}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
